import React, { useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { AppContext } from '../../HomePage';
import { MANAGEMENT_GROUPS, getVisibleManagementPages } from './managementCatalog';
import './ManagementHub.css';

// Per-group color palette (accent / tinted background / border) so each group
// reads as its own block. Keyed by MANAGEMENT_GROUPS[].key.
const GROUP_STYLES = {
  monitoring: { '--hub-accent': '#5b6ef5', '--hub-bg': '#f4f5ff', '--hub-border': '#e2e5fb' },
  analytics:  { '--hub-accent': '#0e9aa7', '--hub-bg': '#eafafb', '--hub-border': '#c6ecef' },
  finance:    { '--hub-accent': '#1f9460', '--hub-bg': '#f1fbf6', '--hub-border': '#cfeede' },
  operations: { '--hub-accent': '#5b6472', '--hub-bg': '#f5f6f8', '--hub-border': '#e3e6ec' },
  scheduling: { '--hub-accent': '#8a4fd6', '--hub-bg': '#f8f3ff', '--hub-border': '#e7d9f8' },
  people:     { '--hub-accent': '#cf7a22', '--hub-bg': '#fff7ee', '--hub-border': '#f3e2cb' },
};
const DEFAULT_GROUP_STYLE = GROUP_STYLES.monitoring;

// Favorites block uses a distinct gold accent and is pinned to the top.
const FAVORITES_STYLE = { '--hub-accent': '#e0a106', '--hub-bg': '#fffaf0', '--hub-border': '#f5e6c4' };

// Favorites are persisted per browser (admins typically use one machine).
const FAVORITES_KEY = 'pux_management_favorites';

const loadFavorites = () => {
  try {
    const raw = window.localStorage.getItem(FAVORITES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const saveFavorites = (favorites) => {
  try {
    window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  } catch (e) {
    // Ignore storage failures (private mode / quota) — favorites are a convenience.
  }
};

const ManagementHub = () => {
  const { t } = React.useContext(AppContext);
  const [query, setQuery] = useState('');
  const [favorites, setFavorites] = useState(loadFavorites);
  // Drag-to-reorder state for the favorites block. `dragKey` is the card being
  // dragged, `dragOverKey` the card it is currently hovering over (for styling).
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const visible = useMemo(() => getVisibleManagementPages(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter(p => {
      const label = t(p.labelKey).toLowerCase();
      const desc = t(p.descKey).toLowerCase();
      return label.includes(q) || desc.includes(q);
    });
  }, [visible, query, t]);

  const goto = (route) => { window.location = '#/' + route; };

  const toggleFavorite = (key) => {
    setFavorites(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      saveFavorites(next);
      return next;
    });
  };

  // Move `fromKey` so it sits next to `toKey`, then persist. Insert after the
  // target when dragging downward and before it when dragging upward, which
  // matches the natural feel of dropping a card past or before another.
  const reorderFavorites = (fromKey, toKey) => {
    if (!fromKey || fromKey === toKey) return;
    setFavorites(prev => {
      const fromIdx = prev.indexOf(fromKey);
      const toIdx = prev.indexOf(toKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      const targetIdx = next.indexOf(toKey);
      const insertAt = fromIdx < toIdx ? targetIdx + 1 : targetIdx;
      next.splice(insertAt, 0, fromKey);
      saveFavorites(next);
      return next;
    });
  };

  // Favorites in their saved order, limited to what the search shows.
  const favoritePages = favorites
    .map(key => filtered.find(p => p.key === key))
    .filter(Boolean);

  // Reordering is only meaningful when the full favorites list is shown; while
  // searching, the block is a filtered subset and drag indexes wouldn't map back.
  const favoritesDraggable = query.trim() === '';

  const groupsToRender = MANAGEMENT_GROUPS
    .map(g => ({ group: g, pages: filtered.filter(p => p.group === g.key) }))
    .filter(x => x.pages.length > 0);

  const renderCard = (p, draggable = false) => {
    const isFav = favorites.includes(p.key);
    const dragClass = draggable
      ? `hub-card--draggable${dragKey === p.key ? ' is-dragging' : ''}${dragOverKey === p.key && dragKey !== p.key ? ' is-drag-over' : ''}`
      : '';
    return (
      <button
        type="button"
        className={`hub-card ${dragClass}`.trim()}
        key={p.key}
        onClick={() => goto(p.route)}
        draggable={draggable}
        onDragStart={draggable ? (e) => {
          setDragKey(p.key);
          e.dataTransfer.effectAllowed = 'move';
          // Firefox requires data to be set for the drag to start.
          e.dataTransfer.setData('text/plain', p.key);
        } : undefined}
        onDragOver={draggable ? (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (dragOverKey !== p.key) setDragOverKey(p.key);
        } : undefined}
        onDragLeave={draggable ? () => {
          setDragOverKey(prev => (prev === p.key ? null : prev));
        } : undefined}
        onDrop={draggable ? (e) => {
          e.preventDefault();
          reorderFavorites(dragKey, p.key);
          setDragKey(null);
          setDragOverKey(null);
        } : undefined}
        onDragEnd={draggable ? () => {
          setDragKey(null);
          setDragOverKey(null);
        } : undefined}
      >
        <i className={`hub-card-icon ${p.icon}`} />
        <div className="hub-card-text">
          <div className="hub-card-title">{t(p.labelKey)}</div>
          <div className="hub-card-desc">{t(p.descKey)}</div>
        </div>
        <span
          className={`hub-card-star ${isFav ? 'is-fav' : ''}`}
          role="button"
          tabIndex={0}
          aria-pressed={isFav}
          aria-label={t('menu.management_hub.favorites')}
          title={t('menu.management_hub.favorites')}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(p.key); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              toggleFavorite(p.key);
            }
          }}
        >
          <i className={isFav ? 'pi pi-star-fill' : 'pi pi-star'} />
        </span>
      </button>
    );
  };

  return (
    <div className="card management-hub">
      <div className="hub-header">
        <h2>{t('menu.management_hub.title')}</h2>
        <p className="hub-subtitle">{t('menu.management_hub.subtitle')}</p>
        <span className="p-input-icon-left hub-search">
          <i className="pi pi-search" />
          <InputText
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('menu.management_hub.search_placeholder')}
          />
        </span>
      </div>

      {groupsToRender.length === 0 && (
        <div className="hub-empty">{t('menu.management_hub.no_results')}</div>
      )}

      {favoritePages.length > 0 && (
        <div className="hub-favorites">
          <div className="hub-group" style={FAVORITES_STYLE}>
            <div className="hub-group-title">
              <i className="pi pi-star-fill" />
              <span>{t('menu.management_hub.favorites')}</span>
            </div>
            <div className="hub-cards">
              {favoritePages.map(p => renderCard(p, favoritesDraggable))}
            </div>
          </div>
        </div>
      )}

      <div className="hub-groups">
        {groupsToRender.map(({ group, pages }) => (
          <div className="hub-group" key={group.key} style={GROUP_STYLES[group.key] || DEFAULT_GROUP_STYLE}>
            <div className="hub-group-title">
              <i className={group.icon} />
              <span>{t(group.titleKey)}</span>
            </div>
            <div className="hub-cards">
              {pages.map(p => renderCard(p))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ManagementHub;
